import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getUserByIdService,
  createUserService,
  updateUserService,
} from "../../services/user/services";
import SubmitButton from "../submitButton/SubmitButton";
import { useNotifications } from "../../context/Notifications";
import { useTranslator } from "../../context/translator";
import Can from "../can/Can";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { ValidatePermission } from "../../utils/allowHelper/AllowHelper";
import { getRolesByCompanyService } from "../../services/roles/services";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const EmployeeEdit = ({ employeeId, teamId }) => {
  const navigate = useNavigate();
  const { translate } = useTranslator();
  const { addNotification } = useNotifications();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const [employee, setEmployee] = useState({
    _id: "",
    firstName: "",
    lastName: "",
    email: "",
    roleId: "",
    ext: "",
    status: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  const handleCancel = () => {
    return navigate(-1);
  };

  const handleOnChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    setEmployee((prevValue) => {
      return {
        ...prevValue,
        [name]: value,
      };
    });
  };

  const handleSubmitEmployee = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    if (employeeId !== "new") {
      const response = await updateUserService({
        employeeId,
        updateFields: employee,
      });

      if (response?.user?.acknowledged) {
        addNotification({
          id: Date.now(),
          message: translate("employee-updated"),
          kind: "success",
        });
        //return true; // navigate(-1);
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-updating-employee"),
          kind: "danger",
        });
      }
    } else {
      const data = {
        teams: teamId,
        companyId: company?._id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        roleId: employee.roleId,
        ext: employee.ext,
        email: employee.email,
      };
      const response = await createUserService({ data });

      if (response?.success) {
        addNotification({
          id: Date.now(),
          message: translate("employee-created"),
          kind: "success",
        });
        //return true; // navigate(-1);
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-creating-employee"),
          kind: "danger",
        });
      }
    }

    setIsLoading(false);
  };

  const getEmployee = React.useCallback(async () => {
    if (employeeId === "new" || !employeeId) return;
    const res = await getUserByIdService({ userId: employeeId });
    const json = await res;

    if (json.code === PERMISSION_CODE.PERMISSION_DENIED) {
      return;
    }
    setEmployee(json.user);
  }, [employeeId]);

  useEffect(() => {
    if (employeeId !== "new") {
      getEmployee();
    }
  }, [employeeId, getEmployee]);

  const getRoles = React.useCallback(async () => {
    let params = {
      companyId: company?._id,
    };

    const data = await getRolesByCompanyService(params);

    setRoles(data?.roles ?? []);
  }, [company?._id]);

  useEffect(() => {
    getRoles();
  }, [getRoles]);

  const readOnly = React.useCallback(() => {
    const hasPermissions =
      ValidatePermission(userProfile?.user, [PERMISSION_CODE.EDIT_USER]) ||
      (ValidatePermission(userProfile?.user, [PERMISSION_CODE.CREATE_USER]) &&
        employeeId === "new");

    return !hasPermissions;
  }, [employeeId, userProfile?.user]);

  const canUpdateRole = React.useCallback(() => {
    const hasPermissions =
      ValidatePermission(userProfile?.user, [
        PERMISSION_CODE.EDIT_USER,
        PERMISSION_CODE.EDIT_ROLE,
      ]) && !employee?.role?.sa;
    return !hasPermissions;
  }, [employee?.role?.sa, userProfile?.user]);

  if (userLoading || companyLoading) return null;

  return (
    <form method="POST" onSubmit={handleSubmitEmployee}>
      <div className="grid gap-6 mb-6 md:grid-cols-2">
        <div>
          <label
            htmlFor="firstName"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("first-name")}
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("name")}
            value={employee?.firstName}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>
        <div>
          <label
            htmlFor="lastName"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("last-name")}
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("last-name")}
            value={employee?.lastName}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>
        <Can permissions={[PERMISSION_CODE.VIEW_ROLES]} key={"view-role"}>
          <Can
            permissions={[PERMISSION_CODE.EDIT_ROLE]}
            key={"edit-role"}
            fallback={
              <>
                <div>
                  <label
                    htmlFor="roleId"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    {translate("select-role")}
                  </label>
                  <input
                    type="text"
                    id="roleId"
                    name="roleId"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder={translate("select-role")}
                    value={employee?.role?.name || ""}
                    disabled={true}
                  />
                </div>
              </>
            }
          >
            <div>
              <label
                htmlFor="roleId"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                {translate("select-role")}
              </label>
              <select
                id="roleId"
                name="roleId"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                value={employee?.roleId}
                onChange={handleOnChange}
                disabled={canUpdateRole() || readOnly()}
              >
                <option value="">{translate("select-role")}</option>
                {employee?.role?.sa && (
                  <option key={employee?.role._id} value={employee?.role._id}>
                    {employee?.role.name}
                  </option>
                )}
                {roles?.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </Can>
        </Can>
        <div>
          <label
            htmlFor="status"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("select-status")}
          </label>
          <select
            id="status"
            name="status"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            defaultValue={employee?.status ? "active" : "deactivate"}
            onChange={handleOnChange}
            disabled={employeeId === "new" || readOnly()}
          >
            <option value="active">{translate("active")}</option>
            <option value="deactivate">{translate("deactivate")}</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="ext"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("extension")}
          </label>
          <input
            type="text"
            id="ext"
            name="ext"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("extension")}
            value={employee?.ext}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>
        <div>
          <label
            htmlFor="email"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Email
          </label>
          <input
            type="text"
            id="email"
            name="email"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Email"
            value={employee?.email}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>
      </div>
      <div className="flex justify-between">
        <button
          type="button"
          className="py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          onClick={handleCancel}
        >
          {translate("cancel")}
        </button>

        <Can
          permissions={[PERMISSION_CODE.EDIT_USER]}
          fallback={
            <Can permissions={[PERMISSION_CODE.CREATE_USER]}>
              <SubmitButton
                isLoading={isLoading}
                text={translate("save")}
                textLoading={translate("saving")}
                key={"saveEmployee"}
              />
            </Can>
          }
        >
          <SubmitButton
            isLoading={isLoading}
            text={translate("save")}
            textLoading={translate("saving")}
            key={"saveEmployee"}
          />
        </Can>
      </div>
    </form>
  );
};

export default EmployeeEdit;
